<script lang="ts" setup>
import { login } from "~/plugins/auth"
import type { DropdownItem } from "#ui/types"
import type { RouteLocationRaw } from "vue-router"

const route = useRoute()

const { t } = useI18n()

const title = computed<string>(() =>
  route.meta.title ? t(`suptitle.${route.meta.title}`) || "" : "",
)
const subtitle_formatter = computed(
  () => (value: string) =>
    R.isTruthy(route.meta.translate_subtitle) ? t(`subtitle.${value}`) : value,
)
const subtitle = computed<string | undefined>(() =>
  route.meta.subtitle ? subtitle_formatter.value(route.meta.subtitle) : undefined,
)
const seoTitle = computed<string>(() => {
  if (route.meta.seoTitle) {
    return t(`seotitle.${route.meta.seoTitle}`)
  }
  if (subtitle.value) {
    return `${title.value} ${subtitle.value}`
  }
  return title.value
})

const showTopBar = computed(() => route.meta.showTopBar || route.meta.showTopBar === undefined)

watchEffect(() => {
  if (seoTitle.value) {
    useSeoMeta({
      title: () => seoTitle.value,
    })
  }
})

const auth0 = useAuth0()
const mainStore = useMainStore()

// Meilisearch index for global search
const meilisearchIndex = useMeilisearchIndex()

// handle main ref
const containerRef: Ref<HTMLElement | null> = ref(null)
provide("containerRef", containerRef)

// define page actions
const pageActions = computed<DropdownItem[][]>(() => {
  const defs = useActionStore().actionDefs
  const actives = route.meta.activeActionKinds || []

  const actions: DropdownItem[][] = R.values(R.pick(defs, actives)).filter(
    (items) => items.length > 0,
  )
  const dropdown_items = R.pipe(
    actions,
    R.map((subgroup) =>
      R.map(subgroup, (item) => ({
        label: t(`components.management.${item.label}`),
        ...R.omit(item, ["label"]),
      })),
    ),
  )

  return dropdown_items
})

// global search
const globalSearchSelected = ref<GlobalSearchResult | undefined>(undefined)
const globalSearchSelectOpts = ref<GlobalSearchSelectOpts>({ isMeta: false })

const typeNames = ["site", "contract", "invoice", "portfolio", "holding", "assetowner"]

const router = useRouter()
watch(
  () => globalSearchSelected.value,
  () => {
    if (R.isNonNullish(globalSearchSelected.value)) {
      let newRoute: RouteLocationRaw | undefined = undefined
      if (globalSearchSelected.value.type === "site") {
        newRoute = `/portfolio/${globalSearchSelected.value.uuid}`
      } else if (globalSearchSelected.value.type === "contract") {
        newRoute = `/contracts/${globalSearchSelected.value.uuid}`
      } else if (globalSearchSelected.value.type === "invoice") {
        newRoute = `/invoices/${globalSearchSelected.value.uuid}`
      } else if (R.isIncludedIn(globalSearchSelected.value.type, ["holding", "assetowner"])) {
        newRoute = `/companies/${globalSearchSelected.value.type}/${globalSearchSelected.value.uuid}`
      } else if (globalSearchSelected.value.type === "portfolio") {
        // WARNING: this doesn't work in case the current route is already /portfolio/...
        newRoute = {
          path: "/portfolio",
          query: { portfolio: globalSearchSelected.value.name },
        }
      } else {
        console.log("unknown type")
      }

      if (R.isNonNullish(newRoute)) {
        if (globalSearchSelectOpts.value.isMeta) {
          const url = router.resolve(newRoute)
          window.open(url.href, "_blank")
        } else {
          router.push(newRoute)
        }
      }
    }
  },
)
</script>

<template>
  <div class="flex h-screen w-screen flex-row">
    <AppSidebar
      v-model:show-global-search="mainStore.showGlobalSearch"
      :is-global-search-enabled="meilisearchIndex !== undefined"
    />
    <UContainer ref="containerRef" class="h-screen w-full min-w-0 max-w-full overflow-auto">
      <header
        class="sticky top-0 z-40 -mx-1 flex h-16 flex-row items-center justify-between bg-white px-1 text-2xl font-semibold"
        :class="showTopBar ? '' : 'hidden'"
      >
        <div class="inline-flex items-center gap-x-2 text-rnxblue-500">
          <h1>{{ title }}</h1>
          <h2 v-if="subtitle" class="max-w-lg truncate text-gray-700 xl:max-w-2xl 3xl:max-w-5xl">
            <span class="pr-2 text-gray-500">:</span>
            <span>{{ subtitle }}</span>
          </h2>
          <div id="teleport-layout-subtitle" class="inline-flex items-center" />
        </div>

        <div class="flex flex-row items-center gap-x-4">
          <div id="teleport-layout-actions" class="inline-flex items-center gap-x-4" />

          <UDropdown
            v-if="pageActions.length"
            class="mb-1.5 font-normal"
            :items="pageActions"
            :ui="{ container: 'z-50', width: 'w-auto max-w-80' }"
          >
            <UButton
              color="rnxblue"
              variant="solid"
              :label="$t('components.actions.actions')"
              trailing-icon="i-heroicons-chevron-down-20-solid"
              :ui="{ icon: { size: { sm: 'w-4 h-4' } } }"
            />
            <template #item="{ item }">
              <div
                v-tippy="{ content: item.label, placement: 'left' }"
                class="flex w-full flex-row gap-x-2"
              >
                <UIcon :name="item.icon" class="size-4 shrink-0 text-gray-400" />
                <span class="truncate">{{ item.label }}</span>
              </div>
            </template>
          </UDropdown>
        </div>
      </header>
      <div
        id="teleport-layout-tabs"
        class="sticky top-16 z-30 -mx-1 mb-4 bg-white px-1"
        :class="showTopBar ? '' : 'hidden'"
      />
      <NuxtPage />

      <!-- additional div for scrolling on bottom -->
      <div class="h-40" />
    </UContainer>
  </div>

  <ClientOnly>
    <UModal
      v-if="meilisearchIndex"
      v-model="mainStore.showGlobalSearch"
      :ui="{
        // keep the global search above any other modal/overlay (legacy SideModal uses z-[100])
        wrapper: 'relative z-[110]',
        width: 'w-[44rem]',
        rounded: 'rounded-xl',
        container: 'flex mt-[15vh] items-start justify-center text-center',
      }"
      :open="mainStore.showGlobalSearch"
    >
      <GlobalSearchContainer
        v-if="meilisearchIndex"
        v-model:selected="globalSearchSelected"
        v-model:selected-opts="globalSearchSelectOpts"
        :index="meilisearchIndex"
        :type-names="typeNames"
        class="rounded-xl border border-gray-200 bg-white shadow-2xl"
        @close="mainStore.showGlobalSearch = false"
      />
    </UModal>
  </ClientOnly>

  <UModal
    :model-value="mainStore.isIdleSession"
    :open="mainStore.isIdleSession"
    :prevent-close="true"
    fullscreen
  >
    <div class="flex h-screen flex-col items-center justify-center bg-gray-100 dark:bg-gray-900">
      <div class="rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <NuxtImg
          provider="imagekit"
          class="mx-auto mb-6 pr-4"
          width="200"
          src="/zefire-spa/logo_renewex.svg"
        />
        <h2 class="mb-4 text-center text-2xl font-bold dark:text-gray-200">Session Timeout</h2>
        <p class="mb-6 text-center text-gray-600 dark:text-gray-400">
          Your session has expired due to inactivity. Please log in again.
        </p>
        <UButton class="mx-auto flex w-72" @click="() => login(auth0, route)">
          <span class="w-full text-center font-semibold">Log In</span>
        </UButton>
      </div>
    </div>
  </UModal>
  <div id="popup" />
  <div id="notifications" />
</template>
